import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type {
  LinkState,
  LinkDetail,
  PlayerLinkSummary,
  CasterMonitoringState,
  PlayerMonitoringState,
  SignalingState,
  SdpState,
  IceDetailState,
  MediaFlowState,
  MediaServerState,
} from '@dronecast/shared'

function createLinkState(): LinkState {
  return { status: 'unknown', updatedAt: Date.now() }
}

function createLinkDetail(): LinkDetail {
  return {
    signaling: { state: 'disconnected', reconnectAttempts: 0 },
    sdp: { state: 'idle' },
    ice: { state: 'idle' },
    mediaFlow: { state: 'idle' },
    mediaServer: { state: 'unknown' },
  }
}

/**
 * Link 상태 결정 로직 (BL-612)
 *
 * 연결 수립 과정(시그널링 → 미디어 서버 → SDP → ICE → 미디어 흐름)의 진행 단계를
 * 반영하여 상태와 사용자 메시지를 결정한다.
 *
 * - `connected`는 시그널링·ICE·미디어 흐름이 모두 정상일 때만 반환 (premature 방지)
 * - 진행 중 단계는 `unknown`으로 두고 단계별 메시지 노출
 */
function computeLinkStatus(detail: LinkDetail, online: boolean): LinkState {
  const now = Date.now()

  // 1. 네트워크 오프라인
  if (!online) {
    return { status: 'disconnected', cause: 'network', causeMessage: '네트워크 연결 없음', updatedAt: now }
  }

  // 2. 시그널링
  if (detail.signaling.state === 'failed') {
    return { status: 'disconnected', cause: 'signaling', causeMessage: '서버 연결 실패', updatedAt: now }
  }
  if (detail.signaling.state === 'disconnected') {
    if (detail.signaling.reconnectAttempts === 0) {
      // 초기 미연결 — 아직 사용자 액션 전
      return { status: 'unknown', causeMessage: '서버 연결 중...', updatedAt: now }
    }
    return { status: 'disconnected', cause: 'signaling', causeMessage: '서버 연결 끊김', updatedAt: now }
  }
  if (detail.signaling.state === 'reconnecting') {
    return { status: 'unstable', cause: 'signaling', causeMessage: '서버 재연결 시도 중...', updatedAt: now }
  }
  if (detail.signaling.state === 'connecting') {
    return { status: 'unknown', cause: 'signaling', causeMessage: '서버 연결 중...', updatedAt: now }
  }

  // 시그널링 connected 이후 단계

  // 3. 미디어 서버
  if (detail.mediaServer.state === 'error') {
    return { status: 'disconnected', cause: 'media_server', causeMessage: '미디어 서버 연결 실패', updatedAt: now }
  }
  if (detail.mediaServer.state === 'degraded') {
    return { status: 'unstable', cause: 'media_server', causeMessage: '미디어 서버 응답 지연', updatedAt: now }
  }

  // 4. SDP 교환
  if (detail.sdp.state === 'failed') {
    return { status: 'disconnected', cause: 'sdp_exchange', causeMessage: '미디어 협상 실패', updatedAt: now }
  }
  if (detail.sdp.state === 'in_progress') {
    return { status: 'unknown', cause: 'sdp_exchange', causeMessage: '미디어 협상 중...', updatedAt: now }
  }

  // 5. ICE 연결
  if (detail.ice.state === 'failed') {
    return { status: 'disconnected', cause: 'ice', causeMessage: '네트워크 경로를 찾을 수 없습니다', updatedAt: now }
  }
  if (detail.ice.state === 'disconnected') {
    return { status: 'unstable', cause: 'ice', causeMessage: '네트워크 경로 일시 단절', updatedAt: now }
  }
  if (detail.ice.state === 'gathering' || detail.ice.state === 'checking') {
    return { status: 'unknown', cause: 'ice', causeMessage: '네트워크 경로 확인 중...', updatedAt: now }
  }
  if (detail.ice.state === 'idle') {
    // SDP 완료 후 ICE 시작 전 — 짧은 과도 상태
    return { status: 'unknown', cause: 'ice', causeMessage: '네트워크 경로 확인 중...', updatedAt: now }
  }

  // ICE connected 이후 단계

  // 6. 미디어 흐름
  if (detail.mediaFlow.state === 'not_flowing') {
    return { status: 'disconnected', cause: 'media_flow', causeMessage: '영상 데이터가 수신되지 않습니다', updatedAt: now }
  }
  if (detail.mediaFlow.state === 'idle') {
    return { status: 'unknown', cause: 'media_flow', causeMessage: '영상 송출 대기 중...', updatedAt: now }
  }

  // 7. 미디어 품질 (flowing 상태에서만 평가)
  if (detail.mediaFlow.packetLoss != null && detail.mediaFlow.packetLoss > 10) {
    return { status: 'unstable', cause: 'media_quality', causeMessage: '영상 품질이 저하되고 있습니다', updatedAt: now }
  }

  // 8. 모든 단계 정상
  return { status: 'connected', updatedAt: now }
}

/**
 * 진단 패널용 연결 이벤트 항목 (BL-603).
 * monitoring.service가 상태 변경 시점에 push하며, 최근 N개만 유지한다.
 */
export interface DiagnosticEvent {
  at: number
  role: 'caster' | 'player'
  segment: 'signaling' | 'sdp' | 'ice' | 'mediaFlow' | 'mediaServer' | 'casterServerLink'
  state: string
  message?: string
}

const HISTORY_CAP = 20

export const useMonitoringStore = defineStore('monitoring', () => {
  // ── 공통 ──
  const online = ref(navigator.onLine)
  const userMessage = ref<string | null>(null)
  /** 연결 이벤트 이력 — 최근 HISTORY_CAP개 (역순: 최신이 [0]) */
  const eventHistory = ref<DiagnosticEvent[]>([])

  // ── Caster 화면용 ──
  const casterDetail = ref<LinkDetail>(createLinkDetail())
  const casterOnAir = ref(false)
  const casterPlayerCount = ref(0)
  const casterPlayers = ref<PlayerLinkSummary[]>([])

  const casterLinkState = computed<LinkState>(() =>
    computeLinkStatus(casterDetail.value, online.value),
  )

  const casterState = computed<CasterMonitoringState>(() => ({
    network: { online: online.value },
    serverLink: { state: casterLinkState.value, detail: casterDetail.value },
    casting: { isOnAir: casterOnAir.value, playerCount: casterPlayerCount.value },
    players: casterPlayers.value,
  }))

  // ── Player 화면용 ──
  const playerDetail = ref<LinkDetail>(createLinkDetail())
  const casterServerLinkState = ref<LinkState>(createLinkState())

  const playerLinkState = computed<LinkState>(() =>
    computeLinkStatus(playerDetail.value, online.value),
  )

  const playerState = computed<PlayerMonitoringState>(() => ({
    network: { online: online.value },
    casterServerLink: { state: casterServerLinkState.value },
    serverPlayerLink: { state: playerLinkState.value, detail: playerDetail.value },
  }))

  // ── 업데이트 메서드 ──

  function updateNetwork(isOnline: boolean) {
    online.value = isOnline
    if (!isOnline) {
      userMessage.value = '인터넷 연결이 끊어졌습니다. 네트워크 상태를 확인해 주세요.'
    } else {
      userMessage.value = null
    }
  }

  function updateSignaling(role: 'caster' | 'player', state: SignalingState, reconnectAttempts = 0) {
    const detail = role === 'caster' ? casterDetail : playerDetail
    detail.value.signaling = { state, reconnectAttempts }

    if (state === 'disconnected' && reconnectAttempts > 0) {
      userMessage.value = '서버와의 연결이 끊어졌습니다. 재연결을 시도합니다.'
    } else if (state === 'connected') {
      userMessage.value = null
    }
  }

  function updateSdp(role: 'caster' | 'player', state: SdpState, elapsedMs?: number) {
    const detail = role === 'caster' ? casterDetail : playerDetail
    detail.value.sdp = { state, elapsedMs }
  }

  function updateIce(role: 'caster' | 'player', state: IceDetailState) {
    const detail = role === 'caster' ? casterDetail : playerDetail
    detail.value.ice = { ...detail.value.ice, state }
  }

  function updateMediaFlow(role: 'caster' | 'player', state: MediaFlowState, stats?: { bitrate?: number; packetLoss?: number; frameRate?: number; jitter?: number }) {
    const detail = role === 'caster' ? casterDetail : playerDetail
    detail.value.mediaFlow = { state, ...stats }
  }

  function updateMediaServer(state: MediaServerState) {
    casterDetail.value.mediaServer = { state }
    playerDetail.value.mediaServer = { state }
  }

  function updateCasting(isOnAir: boolean, playerCount: number) {
    casterOnAir.value = isOnAir
    casterPlayerCount.value = playerCount
  }

  function updatePlayers(players: PlayerLinkSummary[]) {
    casterPlayers.value = players
  }

  function updateCasterServerLink(state: LinkState) {
    casterServerLinkState.value = state
  }

  function setUserMessage(message: string | null) {
    userMessage.value = message
  }

  function pushEvent(event: Omit<DiagnosticEvent, 'at'>) {
    const list = eventHistory.value
    list.unshift({ at: Date.now(), ...event })
    if (list.length > HISTORY_CAP) list.length = HISTORY_CAP
    eventHistory.value = [...list]
  }

  function clearEventHistory() {
    eventHistory.value = []
  }

  function reset() {
    online.value = navigator.onLine
    userMessage.value = null
    casterDetail.value = createLinkDetail()
    casterOnAir.value = false
    casterPlayerCount.value = 0
    casterPlayers.value = []
    playerDetail.value = createLinkDetail()
    casterServerLinkState.value = createLinkState()
    eventHistory.value = []
  }

  return {
    // 상태
    online,
    userMessage,
    casterState,
    playerState,
    casterLinkState,
    playerLinkState,
    casterDetail,
    playerDetail,
    eventHistory,
    // 업데이트
    updateNetwork,
    updateSignaling,
    updateSdp,
    updateIce,
    updateMediaFlow,
    updateMediaServer,
    updateCasting,
    updatePlayers,
    updateCasterServerLink,
    setUserMessage,
    pushEvent,
    clearEventHistory,
    reset,
  }
})
